import React, { useRef } from "react";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Download, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

function redact(email) {
  if (!email || !email.includes("@")) return email;
  const [u, d] = email.split("@");
  return u.slice(0, 2) + "***@" + d;
}

export default function ShareWinCard({ caseData }) {
  const ref = useRef(null);
  const amount = caseData.outcome_amount || caseData.amount;
  const days = caseData.sent_dates?.["1"]
    ? Math.max(1, Math.round((new Date(caseData.updated_at) - new Date(caseData.sent_dates["1"])) / (1000*60*60*24)))
    : null;

  const download = async () => {
    if (!ref.current) return;
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: null, scale: 2 });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `powerhouse-refund-win-${caseData.id.slice(0,8)}.png`;
      a.click();
      toast.success("Card saved. Post it to TikTok or IG!");
    } catch {
      toast.error("Could not generate image. Try again.");
    }
  };

  const nativeShare = async () => {
    if (!ref.current) return;
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: null, scale: 2 });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "refund-win.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "I just got a refund!",
            text: `Just recovered $${amount.toFixed(2)} from ${caseData.merchant_name} with Powerhouse Refunds.`,
          });
        } else {
          download();
        }
      });
    } catch {
      download();
    }
  };

  return (
    <div className="space-y-4">
      <div ref={ref} className="relative overflow-hidden rounded-3xl p-8" style={{
        background: "linear-gradient(135deg, #1E3A8A 0%, #0EA5E9 100%)",
        color: "white",
        width: "100%",
        maxWidth: 480,
        margin: "0 auto",
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div style={{ position: "absolute", top: 24, right: 24, fontSize: 12, opacity: 0.7, letterSpacing: 2, textTransform: "uppercase" }}>
          Powerhouse Refunds
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", padding: "6px 12px", borderRadius: 999, fontSize: 12, marginBottom: 24 }}>
          <Sparkles style={{ width: 14, height: 14 }} /> WIN CONFIRMED
        </div>
        <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.05, marginTop: 8 }}>
          I got ${amount.toFixed(2)}<br />back from<br />{caseData.merchant_name}.
        </div>
        <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 14 }}>
          <div>
            <div style={{ opacity: 0.7, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5 }}>Merchant</div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>{caseData.merchant_name}</div>
          </div>
          <div>
            <div style={{ opacity: 0.7, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5 }}>Outcome</div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>
              {caseData.outcome === "full_refund" ? "Full refund" : caseData.outcome === "partial_refund" ? "Partial refund" : "Resolved"}
            </div>
          </div>
          {days !== null && (
            <div>
              <div style={{ opacity: 0.7, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5 }}>Days</div>
              <div style={{ fontWeight: 600, marginTop: 4 }}>{days} day{days === 1 ? "" : "s"}</div>
            </div>
          )}
          <div>
            <div style={{ opacity: 0.7, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5 }}>Account</div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>{redact(caseData.account_email)}</div>
          </div>
        </div>
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.2)", fontSize: 13, opacity: 0.9 }}>
          Copy-paste refund concierge · powerhouserefunds.com
        </div>
      </div>

      <div className="flex gap-2 justify-center flex-wrap">
        <Button onClick={nativeShare} data-testid="share-win-share-button" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
        <Button onClick={download} variant="outline" data-testid="share-win-download-button" className="rounded-full">
          <Download className="w-4 h-4 mr-2" /> Download PNG
        </Button>
      </div>
    </div>
  );
}
